import React, { useMemo, useState } from "react";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { TopVendorMetric } from "../../../data/models/responses/StatisticsResponses";
import { StatisticsHeader } from "./components/StatisticsHeader";
import { KpiCard } from "./components/KpiCard";
import { TrendChart } from "./components/TrendChart";
import { ConversionCard } from "./components/ConversionCard";
import { TopVendorsLeaderboard } from "./components/TopVendorsLeaderboard";
import { ApprovalFunnel } from "./components/ApprovalFunnel";
import { GoogleAnalyticsPanel } from "./components/GoogleAnalyticsPanel";
import {
  DateRange,
  getPresetRange,
  toIsoDate,
} from "./dateRangePresets";

export function StatisticsScreen() {
  const { statisticsRepository } = useRepositoriesContext();

  const [range, setRange] = useState<DateRange>(() => getPresetRange("30d"));
  const [vendorMetric, setVendorMetric] = useState<TopVendorMetric>("designs");

  const fromIso = useMemo(() => toIsoDate(range.from), [range]);
  const toIso = useMemo(() => toIsoDate(range.to), [range]);

  const overview = statisticsRepository.useOverview(fromIso, toIso);
  const conversion = statisticsRepository.useConversion(fromIso, toIso);
  const approval = statisticsRepository.useApproval(fromIso, toIso);
  const topVendors = statisticsRepository.useTopVendors(
    fromIso,
    toIso,
    vendorMetric,
    10,
  );

  const gaOverview = statisticsRepository.useGoogleAnalytics(
    fromIso,
    toIso,
    "overview",
  );
  const gaTopPages = statisticsRepository.useGoogleAnalytics(
    fromIso,
    toIso,
    "topPages",
  );
  const gaDevices = statisticsRepository.useGoogleAnalytics(
    fromIso,
    toIso,
    "devices",
  );

  const gaIsLoading =
    gaOverview.isLoading || gaTopPages.isLoading || gaDevices.isLoading;

  return (
    <div className="min-h-screen bg-background">
      <style>{`
        @keyframes statsFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: statsFadeIn 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <StatisticsHeader range={range} onRangeChange={setRange} />

        {/* KPI row */}
        <section className="grid grid-cols-12 gap-5 mb-6">
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <KpiCard
              label="Logos created"
              group={overview.data?.logos}
              isLoading={overview.isLoading}
              accentHex="#1B76DF"
              delayMs={0}
            />
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <KpiCard
              label="Designs created"
              group={overview.data?.designs}
              isLoading={overview.isLoading}
              accentHex="#7BBB58"
              delayMs={80}
            />
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <KpiCard
              label="Orders placed"
              group={overview.data?.orders}
              isLoading={overview.isLoading}
              accentHex="#E7B400"
              delayMs={160}
            />
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3">
            <KpiCard
              label="New users"
              group={overview.data?.users}
              isLoading={overview.isLoading}
              accentHex="#9333EA"
              delayMs={240}
            />
          </div>
        </section>

        {/* Trend chart + conversion sidebar */}
        <section className="grid grid-cols-12 gap-5 mb-6">
          <div className="col-span-12 lg:col-span-8">
            <TrendChart overview={overview.data} isLoading={overview.isLoading} />
          </div>
          <div className="col-span-12 lg:col-span-4">
            <ConversionCard data={conversion.data} isLoading={conversion.isLoading} />
          </div>
        </section>

        {/* Leaderboard + approval funnel */}
        <section className="grid grid-cols-12 gap-5 mb-10">
          <div className="col-span-12 lg:col-span-7">
            <TopVendorsLeaderboard
              data={topVendors.data}
              isLoading={topVendors.isLoading}
              metric={vendorMetric}
              onMetricChange={setVendorMetric}
            />
          </div>
          <div className="col-span-12 lg:col-span-5">
            <ApprovalFunnel data={approval.data} isLoading={approval.isLoading} />
          </div>
        </section>

        {/* Google Analytics */}
        <section className="grid grid-cols-12 gap-5">
          <GoogleAnalyticsPanel
            overview={gaOverview.data}
            topPages={gaTopPages.data}
            devices={gaDevices.data}
            isLoading={gaIsLoading}
          />
        </section>
      </div>
    </div>
  );
}
